import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { API_BASE_URL } from '../../config';
import './QuickBooksSettings.css';

type CampusKey = 'madison' | 'athens';

type StatusPayload = {
  enabled: boolean;
  oauthConfigured: boolean;
  env: string;
  campuses: Array<{
    campusKey: CampusKey;
    label: string;
    connection: null | {
      realmId: string;
      companyName: string | null;
      connectedAt: string | null;
    };
  }>;
  settings: { emailInvoicesAuto: boolean };
};

type CustomerRow = {
  tillyCustomerId: number;
  tillyName: string;
  mapped: boolean;
  qboCustomerId: string | null;
  qboDisplayName: string | null;
  qboBillingEmail: string | null;
  emailOnFileLabel: string;
  emailFromQbo: boolean;
  suggested: null | { qboCustomerId: string; qboDisplayName: string | null; qboBillingEmail: string | null };
};

type ItemRow = {
  tillyItemName: string;
  mapped: boolean;
  qboItemId: string | null;
  qboName: string | null;
  qboType: string | null;
  suggested: null | { qboItemId: string; qboName: string | null; qboType: string | null };
  isKegDeposit?: boolean;
};

type DepositSizeRow = {
  kegSize: string;
  mapped: boolean;
  qboItemId: string | null;
  qboName: string | null;
  qboType: string | null;
};

type DepositCustomerRow = {
  tillyCustomerId: number;
  tillyName: string;
  sizes: DepositSizeRow[];
};

type DepositMapsPayload = {
  kegSizes: string[];
  genericFallback: {
    mapped: boolean;
    qboItemId: string | null;
    qboName: string | null;
    qboType: string | null;
  };
  customers: DepositCustomerRow[];
  qboItems: Array<{ id: string; name: string; type: string | null }>;
};

type Tab = 'connection' | 'customers' | 'items' | 'unsynced' | 'older';

const QuickBooksSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [campusKey, setCampusKey] = useState<CampusKey>('madison');
  const [tab, setTab] = useState<Tab>('connection');
  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [qboCustomers, setQboCustomers] = useState<Array<{ id: string; displayName: string; billingEmail: string | null }>>([]);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [qboItems, setQboItems] = useState<Array<{ id: string; name: string; type: string | null }>>([]);
  const [depositMaps, setDepositMaps] = useState<DepositMapsPayload | null>(null);
  const [incomeAccounts, setIncomeAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [unsynced, setUnsynced] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [backfillPreview, setBackfillPreview] = useState<any>(null);

  const loadStatus = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/status`);
    if (res.status === 404) {
      setDisabled(true);
      setStatus(null);
      return;
    }
    if (!res.ok) throw new Error(`Could not load QuickBooks status (${res.status})`);
    setDisabled(false);
    setStatus(await res.json());
  }, []);

  const loadCustomers = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/customers?campusKey=${campusKey}`);
    if (!res.ok) throw new Error('Could not load customer mapping');
    const data = await res.json();
    setCustomers(data.customers || []);
    setQboCustomers(data.qboCustomers || []);
  }, [campusKey]);

  const loadItems = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/items?campusKey=${campusKey}`);
    if (!res.ok) throw new Error('Could not load item mapping');
    const data = await res.json();
    setItems(data.items || []);
    setQboItems(data.qboItems || []);
  }, [campusKey]);

  const loadDepositMaps = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/deposit-maps?campusKey=${campusKey}`);
    if (!res.ok) throw new Error('Could not load keg deposit mapping');
    setDepositMaps(await res.json());
  }, [campusKey]);

  const loadUnsynced = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/unsynced`);
    if (!res.ok) throw new Error('Could not load unsynced invoices');
    const data = await res.json();
    setUnsynced(data.invoices || []);
  }, []);

  useEffect(() => {
    const qbo = searchParams.get('qbo');
    const message = searchParams.get('message');
    if (qbo === 'connected') {
      setSuccess('QuickBooks connected.');
      setSearchParams({}, { replace: true });
    } else if (qbo === 'error') {
      setError(message || 'QuickBooks connect failed');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    void loadStatus().catch((err: Error) => setError(err.message));
  }, [loadStatus]);

  useEffect(() => {
    if (disabled || !status) return;
    if (tab === 'customers') void loadCustomers().catch((e: Error) => setError(e.message));
    if (tab === 'items') {
      void loadItems().catch((e: Error) => setError(e.message));
      void loadDepositMaps().catch((e: Error) => setError(e.message));
      void apiFetch(`${API_BASE_URL}/api/quickbooks/income-accounts?campusKey=${campusKey}`)
        .then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            setIncomeAccounts(data.accounts || []);
          }
        })
        .catch(() => undefined);
    }
    if (tab === 'unsynced') void loadUnsynced().catch((e: Error) => setError(e.message));
  }, [tab, campusKey, disabled, status, loadCustomers, loadItems, loadDepositMaps, loadUnsynced]);

  const connect = async (applyToBoth: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/quickbooks/connect?campusKey=${campusKey}&applyToBoth=${applyToBoth ? '1' : '0'}`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not start QuickBooks connect');
      }
      const data = await res.json();
      window.location.href = data.authorizeUrl;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Connect failed');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect QuickBooks for ${campusKey === 'madison' ? 'Madison' : 'Athens'}?`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campusKey }),
      });
      if (!res.ok) throw new Error('Disconnect failed');
      setSuccess('Disconnected.');
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Disconnect failed');
    } finally {
      setBusy(false);
    }
  };

  const saveEmailAuto = async (enabled: boolean) => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailInvoicesAuto: enabled }),
      });
      if (!res.ok) throw new Error('Could not save email setting');
      await loadStatus();
      setSuccess(enabled ? 'Automatic QuickBooks email is ON.' : 'Automatic QuickBooks email is OFF.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const mapCustomer = async (row: CustomerRow, qboCustomerId: string) => {
    const qbo = qboCustomers.find((c) => c.id === qboCustomerId);
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/customers/${row.tillyCustomerId}/map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campusKey,
          qboCustomerId,
          qboDisplayName: qbo?.displayName,
          qboBillingEmail: qbo?.billingEmail,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Map failed');
      }
      await loadCustomers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Map failed');
    } finally {
      setBusy(false);
    }
  };

  const createCustomer = async (row: CustomerRow) => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/customers/${row.tillyCustomerId}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campusKey }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Create in QuickBooks failed');
      }
      setSuccess(`Created ${row.tillyName} in QuickBooks.`);
      await loadCustomers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleCustomerEmail = async (row: CustomerRow, enabled: boolean) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/quickbooks/customers/${row.tillyCustomerId}/email-from-qbo`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campusKey, enabled }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not update email setting');
      }
      await loadCustomers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const mapItem = async (row: ItemRow, qboItemId: string) => {
    const qbo = qboItems.find((i) => i.id === qboItemId);
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/items/map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campusKey,
          tillyItemName: row.tillyItemName,
          qboItemId,
          qboName: qbo?.name,
          qboType: qbo?.type,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Map failed');
      }
      await loadItems();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Map failed');
    } finally {
      setBusy(false);
    }
  };

  const mapDeposit = async (customerId: number, kegSize: string, qboItemId: string) => {
    const qbo = (depositMaps?.qboItems || qboItems).find((i) => i.id === qboItemId);
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/deposit-maps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campusKey,
          tillyCustomerId: customerId,
          kegSize,
          qboItemId,
          qboName: qbo?.name,
          qboType: qbo?.type,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Deposit map failed');
      }
      await loadDepositMaps();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Deposit map failed');
    } finally {
      setBusy(false);
    }
  };

  const clearDeposit = async (customerId: number, kegSize: string) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/quickbooks/deposit-maps?campusKey=${encodeURIComponent(campusKey)}&tillyCustomerId=${customerId}&kegSize=${encodeURIComponent(kegSize)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not clear deposit map');
      }
      await loadDepositMaps();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Clear failed');
    } finally {
      setBusy(false);
    }
  };

  const createItem = async (row: ItemRow, incomeAccountId: string) => {
    if (!incomeAccountId) {
      setError('Pick an income account first.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/items/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campusKey,
          tillyItemName: row.tillyItemName,
          incomeAccountId,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Create item failed');
      }
      setSuccess(`Created "${row.tillyItemName}" as a non-inventory item in QuickBooks.`);
      await loadItems();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async (invoiceId: number) => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/invoices/${invoiceId}/sync`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) throw new Error(body.error || 'Sync failed');
      setSuccess(`Synced invoice ${body.qboInvoiceId ? '' : ''}#${invoiceId}.`);
      await loadUnsynced();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sync failed');
      await loadUnsynced();
    } finally {
      setBusy(false);
    }
  };

  const previewOlder = async () => {
    setBusy(true);
    setBackfillPreview(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/backfill/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromDate, toDate }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Preview failed');
      setBackfillPreview(body);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const runOlder = async () => {
    if (!backfillPreview) return;
    if (!window.confirm(`Send ${backfillPreview.syncCount} older invoice(s) to QuickBooks?`)) return;
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/quickbooks/backfill/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromDate, toDate }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Send failed');
      setSuccess(`Sent ${body.synced} invoice(s). ${body.failed ? `${body.failed} failed.` : ''}`);
      setBackfillPreview(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  const campusConn = status?.campuses?.find((c) => c.campusKey === campusKey)?.connection;

  return (
    <div className="page-container container qbo-settings" data-testid="quickbooks-settings">
      <h2 className="qbo-breadcrumb">
        <Link to="/settings" className="qbo-crumb-link">
          Settings
        </Link>
        <span className="qbo-crumb-sep" aria-hidden="true">
          ›
        </span>
        <span className="qbo-crumb-current">QuickBooks</span>
      </h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button type="button" className="btn btn-sm btn-link" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
          <button type="button" className="btn btn-sm btn-link" onClick={() => setSuccess(null)}>
            Dismiss
          </button>
        </div>
      )}

      {disabled ? (
        <p>
          QuickBooks sync is turned off. On Render, set <code>QUICKBOOKS_SYNC=1</code> (and the Intuit
          app env vars), then redeploy.
        </p>
      ) : (
        <>
          <div className="qbo-campus-row">
            <label>
              Site{' '}
              <select
                className="form-select form-select-sm d-inline-block w-auto"
                value={campusKey}
                onChange={(e) => setCampusKey(e.target.value as CampusKey)}
              >
                <option value="madison">Madison</option>
                <option value="athens">Athens</option>
              </select>
            </label>
            <span className="text-muted small">
              Mappings are per site so Madison and Athens can use different QuickBooks companies later.
            </span>
          </div>

          <div className="qbo-tabs" role="tablist">
            {(
              [
                ['connection', 'Connection'],
                ['customers', 'Customers'],
                ['items', 'Items'],
                ['unsynced', 'Unsynced invoices'],
                ['older', 'Send older invoices'],
              ] as Array<[Tab, string]>
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                className={`qbo-tab${tab === id ? ' active' : ''}`}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'connection' && (
            <section className="qbo-panel">
              <p>
                When a sales order is approved and Tilly creates the invoice, the same invoice is
                created in QuickBooks (one-way). Inventory is never touched in QuickBooks.
              </p>
              <p>
                Environment: <strong>{status?.env || 'sandbox'}</strong>
                {!status?.oauthConfigured && (
                  <span className="text-danger">
                    {' '}
                    — Intuit app env vars are missing (QBO_CLIENT_ID, QBO_CLIENT_SECRET, QBO_REDIRECT_URI).
                  </span>
                )}
              </p>
              {campusConn ? (
                <div className="qbo-connected">
                  <p>
                    Connected to <strong>{campusConn.companyName || campusConn.realmId}</strong>
                    {campusConn.connectedAt ? ` · since ${campusConn.connectedAt}` : ''}
                  </p>
                  <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={() => void disconnect()}>
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="qbo-connect-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !status?.oauthConfigured}
                    onClick={() => void connect(true)}
                  >
                    Connect QuickBooks
                  </button>
                  <p className="small text-muted">
                    Connects this company for Madison and Athens. You can disconnect one site later if
                    you need separate companies.
                  </p>
                </div>
              )}

              <h3 className="h5 mt-4">Email invoice from QuickBooks automatically</h3>
              <p className="small">
                Off by default. When on, Tilly asks QuickBooks to email the invoice after it is created
                — only for customers that also have email turned on below. Edits and retries never
                re-send if it was already emailed.
              </p>
              <label className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={!!status?.settings?.emailInvoicesAuto}
                  disabled={busy}
                  onChange={(e) => void saveEmailAuto(e.target.checked)}
                />
                <span className="form-check-label">Email invoice from QuickBooks automatically (global)</span>
              </label>
            </section>
          )}

          {tab === 'customers' && (
            <section className="qbo-panel">
              <div className="qbo-toolbar">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  disabled={busy}
                  onClick={() =>
                    void apiFetch(`${API_BASE_URL}/api/quickbooks/customers/accept-suggested`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ campusKey }),
                    }).then(() => loadCustomers())
                  }
                >
                  Accept name matches
                </button>
              </div>
              <ul className="qbo-map-list">
                {customers.map((row) => (
                  <li key={row.tillyCustomerId} className="qbo-map-card">
                    <div className="qbo-map-title">{row.tillyName}</div>
                    {row.mapped ? (
                      <p className="mb-1">
                        Mapped to <strong>{row.qboDisplayName || row.qboCustomerId}</strong>
                      </p>
                    ) : (
                      <p className="mb-1 text-warning">Not mapped</p>
                    )}
                    <p className="small mb-2">QuickBooks email: {row.emailOnFileLabel}</p>
                    {!row.mapped && (
                      <div className="qbo-map-actions">
                        <select
                          className="form-select form-select-sm"
                          defaultValue={row.suggested?.qboCustomerId || ''}
                          onChange={(e) => {
                            if (e.target.value) void mapCustomer(row, e.target.value);
                          }}
                        >
                          <option value="">Pick QuickBooks customer…</option>
                          {qboCustomers.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.displayName}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={busy}
                          onClick={() => void createCustomer(row)}
                        >
                          Create in QuickBooks
                        </button>
                      </div>
                    )}
                    {row.mapped && (
                      <label className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={row.emailFromQbo}
                          disabled={busy || !row.qboBillingEmail}
                          onChange={(e) => void toggleCustomerEmail(row, e.target.checked)}
                        />
                        <span className="form-check-label">
                          Email invoice from QuickBooks automatically for this customer
                          {!row.qboBillingEmail ? ' (no email on file)' : ''}
                        </span>
                      </label>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {tab === 'items' && (
            <section className="qbo-panel">
              <p className="small">
                Map each Tilly product + package to a QuickBooks <strong>Service</strong> or{' '}
                <strong>Non-inventory</strong> item only — never Inventory.
              </p>
              <div className="qbo-toolbar">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  disabled={busy}
                  onClick={() =>
                    void apiFetch(`${API_BASE_URL}/api/quickbooks/items/accept-suggested`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ campusKey }),
                    }).then(() => loadItems())
                  }
                >
                  Accept name matches
                </button>
              </div>
              <ul className="qbo-map-list">
                {items.map((row) => (
                  <ItemMapRow
                    key={row.tillyItemName}
                    row={row}
                    qboItems={qboItems}
                    incomeAccounts={incomeAccounts}
                    busy={busy}
                    onMap={(id) => void mapItem(row, id)}
                    onCreate={(accountId) => void createItem(row, accountId)}
                  />
                ))}
              </ul>

              <h3 className="h5 mt-4">Keg deposits by distributor and size</h3>
              <p className="small">
                QuickBooks often uses separate lease/deposit items per distributor and keg size (for
                example Gulf Sixth BBL Keg Lease). Map those here. If a size has no map, sync falls
                back to the generic <strong>Keg Deposit</strong> item above — never creates QuickBooks
                items automatically.
              </p>
              {depositMaps?.genericFallback?.mapped ? (
                <p className="small mb-2">
                  Generic fallback: <strong>{depositMaps.genericFallback.qboName || 'Keg Deposit'}</strong>
                </p>
              ) : (
                <p className="small text-warning mb-2">
                  Generic Keg Deposit is not mapped — each distributor + size used on an invoice must
                  be mapped or sync will fail.
                </p>
              )}
              <ul className="qbo-map-list">
                {(depositMaps?.customers || []).map((cust) => (
                  <li key={cust.tillyCustomerId} className="qbo-map-card">
                    <div className="qbo-map-title">{cust.tillyName}</div>
                    <ul className="qbo-deposit-size-list">
                      {cust.sizes.map((sizeRow) => (
                        <li key={sizeRow.kegSize} className="qbo-deposit-size-row">
                          <span className="qbo-deposit-size-label">{sizeRow.kegSize}</span>
                          {sizeRow.mapped ? (
                            <span className="qbo-deposit-size-mapped">
                              {sizeRow.qboName || sizeRow.qboItemId}
                              <button
                                type="button"
                                className="btn btn-sm btn-link"
                                disabled={busy}
                                onClick={() => void clearDeposit(cust.tillyCustomerId, sizeRow.kegSize)}
                              >
                                Clear
                              </button>
                            </span>
                          ) : (
                            <select
                              className="form-select form-select-sm"
                              defaultValue=""
                              disabled={busy}
                              aria-label={`Map ${sizeRow.kegSize} for ${cust.tillyName}`}
                              onChange={(e) => {
                                if (e.target.value) {
                                  void mapDeposit(cust.tillyCustomerId, sizeRow.kegSize, e.target.value);
                                }
                              }}
                            >
                              <option value="">Pick QuickBooks item…</option>
                              {(depositMaps?.qboItems || qboItems).map((i) => (
                                <option key={i.id} value={i.id}>
                                  {i.name} ({i.type})
                                </option>
                              ))}
                            </select>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {tab === 'unsynced' && (
            <section className="qbo-panel">
              <p className="small">Invoices that are not synced yet, or that failed. Tap Sync now to retry.</p>
              {unsynced.length === 0 ? (
                <p>No unsynced invoices.</p>
              ) : (
                <ul className="qbo-map-list">
                  {unsynced.map((inv) => (
                    <li key={inv.invoiceId} className="qbo-map-card">
                      <div className="qbo-map-title">
                        {inv.invoiceNumber || `#${inv.invoiceId}`} · {inv.customerName}
                      </div>
                      <p className="small mb-2">
                        {inv.sync?.label || inv.qboSyncStatus || 'Not synced'}
                        {inv.qboSyncError ? ` — ${inv.qboSyncError}` : ''}
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busy}
                        onClick={() => void syncNow(inv.invoiceId)}
                      >
                        Sync now
                      </button>
                      <Link className="btn btn-sm btn-link" to={`/invoices/${inv.invoiceId}`}>
                        Open invoice
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {tab === 'older' && (
            <section className="qbo-panel">
              <p className="small">
                Historical invoices are not sent automatically. Preview a date range first, then send.
              </p>
              <div className="qbo-date-row">
                <label>
                  From{' '}
                  <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </label>
                <label>
                  To{' '}
                  <input type="date" className="form-control form-control-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </label>
                <button type="button" className="btn btn-secondary" disabled={busy || !fromDate || !toDate} onClick={() => void previewOlder()}>
                  Preview
                </button>
              </div>
              {backfillPreview && (
                <div className="mt-3">
                  <p>
                    {backfillPreview.syncCount} to send · {backfillPreview.skipCount} skip ·{' '}
                    {backfillPreview.alreadySyncedCount} already synced
                  </p>
                  <ul className="small">
                    {(backfillPreview.invoices || []).slice(0, 40).map((inv: any) => (
                      <li key={inv.invoiceId}>
                        {inv.invoiceNumber} · {inv.customerName} · {inv.action}
                        {inv.reason ? ` (${inv.reason})` : ''}
                      </li>
                    ))}
                  </ul>
                  {backfillPreview.syncCount > 0 && (
                    <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void runOlder()}>
                      Send older invoices
                    </button>
                  )}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

function ItemMapRow({
  row,
  qboItems,
  incomeAccounts,
  busy,
  onMap,
  onCreate,
}: {
  row: ItemRow;
  qboItems: Array<{ id: string; name: string; type: string | null }>;
  incomeAccounts: Array<{ id: string; name: string }>;
  busy: boolean;
  onMap: (id: string) => void;
  onCreate: (accountId: string) => void;
}) {
  const [accountId, setAccountId] = useState('');
  return (
    <li className="qbo-map-card">
      <div className="qbo-map-title">
        {row.tillyItemName}
        {row.isKegDeposit ? ' (keg deposit)' : ''}
      </div>
      {row.mapped ? (
        <p className="mb-1">
          Mapped to <strong>{row.qboName || row.qboItemId}</strong> ({row.qboType || 'item'})
        </p>
      ) : (
        <>
          <p className="mb-1 text-warning">Not mapped</p>
          <div className="qbo-map-actions">
            <select
              className="form-select form-select-sm"
              defaultValue={row.suggested?.qboItemId || ''}
              onChange={(e) => {
                if (e.target.value) onMap(e.target.value);
              }}
            >
              <option value="">Pick QuickBooks item…</option>
              {qboItems.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} ({i.type})
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">Income account for new item…</option>
              {incomeAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={busy || !accountId}
              onClick={() => onCreate(accountId)}
            >
              Create as non-inventory item
            </button>
          </div>
        </>
      )}
    </li>
  );
}

export default QuickBooksSettings;
